import { useEffect, useRef, useState } from 'react';
import { Zap, BarChart3, Trophy, Bell, Play, Film } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';

export default function VideoSection() {
  const [headerRef, headerVisible] = useScrollReveal();
  const sectionRef = useRef(null);
  const [autoPlayed, setAutoPlayed] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !autoPlayed) {
          setAutoPlayed(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [autoPlayed]);

  return (
    <section id="video-section" className="video-section-hero" ref={sectionRef}>
      <div className="video-bg-effects">
        <div className="video-orb video-orb-1"></div>
        <div className="video-orb video-orb-2"></div>
      </div>

      <div className={`video-header reveal ${headerVisible ? 'visible' : ''}`} ref={headerRef}>
        <span className="section-label">Decouvrez TIGOO</span>
        <h2 className="section-title">Voyez comment ca fonctionne</h2>
        <p className="section-sub">Une demonstration en quelques secondes vaut mieux qu'un long discours.</p>
      </div>

      <div className="video-showcase">
        <div className="video-glow"></div>

        <div className="video-frame-hero">
          {!autoPlayed ? (
            <div className="video-poster">
              <div className="poster-overlay"></div>
              <div className="poster-lines">
                <div className="pline pline-1"></div>
                <div className="pline pline-2"></div>
                <div className="pline pline-3"></div>
              </div>
              <div className="poster-label">
                <span className="poster-icon"><Film size={36} /></span>
                <span>Dêcouvrir TIGOO en action</span>
                <small>La video demarre automatiquement</small>
              </div>
              <button className="play-btn" onClick={() => setAutoPlayed(true)} aria-label="Lire la video">
                <div className="play-ring play-ring-1"></div>
                <div className="play-ring play-ring-2"></div>
                <div className="play-icon">
                  <Play size={22} fill="currentColor" />
                </div>
              </button>
              <div className="video-badge"><Play size={10} /> Demo · 1 min 30</div>
            </div>
          ) : (
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1&mute=1&loop=1&playlist=dQw4w9WgXcQ&controls=1&rel=0"
              title="Demo TIGOO"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: '20px' }}
            ></iframe>
          )}
        </div>

        <div className="video-features-row">
          {[
            { icon: <Zap size={20} />, text: 'Coupons en temps réel', sub: 'Recevez instantanément' },
            { icon: <BarChart3 size={20} />, text: 'Stats detaillees', sub: 'Performances vérifiees' },
            { icon: <Trophy size={20} />, text: 'Top pronostiqueurs', sub: 'Classement transparent' },
            { icon: <Bell size={20} />, text: 'Alertes instantanees', sub: 'Ne manquez rien' },
          ].map((item, i) => (
            <FeaturePill key={i} item={item} delay={i * 0.12} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturePill({ item, delay }) {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      className={`video-feature-pill ${isVisible ? 'visible' : ''}`}
      ref={ref}
      style={{ animationDelay: `${delay}s` }}
    >
      <span className="vf-icon">{item.icon}</span>
      <div className="vf-text">
        <strong>{item.text}</strong>
        <small>{item.sub}</small>
      </div>
    </div>
  );
}
